import { z } from 'zod';
import {
  COLUMN_SUFFIXES,
  fieldEnumType,
  type PhysicalColumnRole,
  type PhysicalColumnSpec,
  staticType,
} from './field-storage';
import { maxFilesOf } from './files';
import { SELECT_OPTION_ID_PATTERN } from './ids';

/**
 * INV-12 TYPE-LEVEL EXHAUSTIVENESS.
 *
 * Adding a field type is exactly one registry entry. The `satisfies
 * Record<FieldType, FieldTypeDef>` at the bottom turns a forgotten entry into a
 * compile error.
 *
 * All THIRTEEN entries are complete: physical column layout, value validation,
 * filter operators, the two TEXT members the CSV module added
 * (`docs/decisions/csv-import-export.md` D8, D37) - `fromText` reading a cell,
 * `toText` writing one - and the two the group-by module added
 * (`docs/decisions/group-by-aggregation.md` D14) - `groupable` saying whether the
 * type may be a dimension, `aggregates` listing what may be computed over it.
 * Storage choices for the four types that were open in
 * Phase 1 are locked in `docs/decisions/field-types.md`:
 *  - MULTI_SELECT: array of the field's PG enum type, one column
 *  - RELATION:     all three kinds; only MANY_TO_ONE owns a column
 *  - PHONE:        canonical E.164 plus the original input, two columns
 *  - CURRENCY:     amount_micros bigint plus currency_code char(3), two columns
 *
 * FILES is the thirteenth (`docs/decisions/file-attachments.md` D10, D12) and it answers
 * two members differently from the other twelve, which is why they are named here rather
 * than discovered: `fromText` ALWAYS refuses, because a CSV can carry a filename but not
 * the file (D12); and `aggregates` is TWO operations rather than the universal three,
 * because `countUnique` over a jsonb array counts distinct attachment SETS, which is a
 * number no user asked for and no screen can label (D10).
 *
 * SELECT / MULTI_SELECT options are `{id, label, color?}` and the STORED value is
 * the immutable option id (field-types.md, amended 2026-07-27). Labels never
 * reach the database schema, which is what makes an option rename free.
 */

export const FIELD_TYPES = [
  'TEXT',
  'NUMBER',
  'BOOLEAN',
  'DATE',
  'DATETIME',
  'SELECT',
  'MULTI_SELECT',
  'RELATION',
  'EMAIL',
  'PHONE',
  'URL',
  'CURRENCY',
  'FILES',
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

/**
 * The complete operand vocabulary. INV-6 ONE FILTER SEMANTICS: this list is the
 * single source of truth for both the SQL compiler and the JS evaluator, so a
 * new operator cannot be added to one backend only.
 */
export const FILTER_OPERATORS = [
  'eq',
  'neq',
  'lt',
  'lte',
  'gt',
  'gte',
  'contains',
  'notContains',
  /** Array fields: overlaps the given set (PostgreSQL `&&`). */
  'containsAny',
  'startsWith',
  'endsWith',
  'in',
  'notIn',
  'before',
  'after',
  'between',
  'isEmpty',
  'isNotEmpty',
] as const;

export type FilterOperator = (typeof FILTER_OPERATORS)[number];

/** Operators that carry no operand; a value must NOT be supplied. */
export const NULLARY_FILTER_OPERATORS = ['isEmpty', 'isNotEmpty'] as const;
export type NullaryFilterOperator = (typeof NULLARY_FILTER_OPERATORS)[number];

export function isNullaryOperator(operator: FilterOperator): operator is NullaryFilterOperator {
  return (NULLARY_FILTER_OPERATORS as readonly FilterOperator[]).includes(operator);
}

/**
 * How a field type's VALUES compare. Two field types in the same family accept the
 * same operand shapes and are compared the same way, whatever their storage.
 *
 * Separate from the operator list on purpose: TEXT and PHONE share the textual
 * operators but not the storage, while SELECT and MULTI_SELECT share the `option`
 * family and declare different operators. Both backends switch on the family to
 * decide how to READ a value and on the operator to decide what to ASK.
 */
export const FILTER_FAMILIES = [
  'text',
  'number',
  'currency',
  'boolean',
  'date',
  'datetime',
  'option',
  'id',
  /**
   * FILES, and a NINTH family rather than a borrowed one (`file-attachments.md` D10).
   *
   * Borrowing `'id'` would compile and would never misbehave, because FILES declares only
   * nullary operators and the family is therefore never consulted for operand
   * normalisation. It would still be a lie in a table somebody reads. The ninth family
   * costs one arm in `normalizeCondition` that refuses, and that arm is a better artefact
   * than a correct-by-accident label.
   */
  'files',
] as const;

export type FilterFamily = (typeof FILTER_FAMILIES)[number];

// --- grouping and aggregation (group-by-aggregation.md D12, D14) ------------

/**
 * HOW A FIELD TYPE MAY BE A GROUP DIMENSION (`docs/decisions/group-by-aggregation.md`
 * D11).
 *
 * `value` groups by the stored value, `bucketed` by a date bucket (D13), `none` refuses.
 * The rule behind the table is one sentence: a dimension must have a bounded number of
 * values AND every one of its groups must be expressible as a filter condition on the
 * same field. The second half is what makes "show more rows in this group" an ordinary
 * `POST /api/query` instead of a second cursor grammar (D19).
 */
export const GROUPABLE_KINDS = ['none', 'value', 'bucketed'] as const;
export type GroupableKind = (typeof GROUPABLE_KINDS)[number];

/**
 * THE AGGREGATE VOCABULARY, CLOSED AT TEN (D12).
 *
 * It lives beside the registry for the reason `FILTER_OPERATORS` does: the registry below
 * declares which of these each field type accepts, so the vocabulary and the per-type
 * table cannot be edited apart.
 *
 * `count` is deliberately in no type's list. It is `count(*)` over the group itself, it
 * references no field, and it therefore passes no field permission check - which is
 * exactly the `withTotalCount` rule, bounded by the object check and the scope predicate
 * like any other row count (D7). Every other operation names a field.
 *
 * `min` and `max` over a DATE or a DATETIME are LABELLED "Earliest" and "Latest" in the
 * UI: same SQL, different word, because nobody says "the minimum close date".
 *
 * NOT HERE, permanently: percent-empty and percent-filled. Every researched product ships
 * them as operations, and computing them in SQL as ratios in 0..1 is a live formatting
 * trap. `count` and `countEmpty` are in every response anyway, so a percentage is a
 * division the display layer does with no round trip and no unit ambiguity (D12
 * semantic decision 1).
 */
export const AGGREGATE_OPS = [
  'count',
  'countValues',
  'countEmpty',
  'countUnique',
  'sum',
  'avg',
  'min',
  'max',
  'countTrue',
  'countFalse',
] as const;

export type AggregateOp = (typeof AGGREGATE_OPS)[number];

/**
 * THE COUNT FAMILY, and the split is a rendering rule rather than a taxonomy (D12
 * semantic decisions 2 and 3).
 *
 * Over zero rows `count(*)` is 0 while `sum` is SQL NULL, and both are the correct answer
 * to their own question - so nothing is ever COALESCEd. A count-family operation renders
 * `0`; `sum`, `avg`, `min` and `max` render an em dash, because there is nothing to
 * average. The same split is what a synthesised empty group is built from (D29).
 */
export const COUNT_AGGREGATE_OPS = [
  'count',
  'countValues',
  'countEmpty',
  'countUnique',
  'countTrue',
  'countFalse',
] as const satisfies readonly AggregateOp[];

export function isCountAggregateOp(op: AggregateOp): boolean {
  return (COUNT_AGGREGATE_OPS as readonly AggregateOp[]).includes(op);
}

// --- per-field configuration ------------------------------------------------

export const RELATION_KINDS = ['MANY_TO_ONE', 'ONE_TO_MANY', 'MANY_TO_MANY'] as const;
export type RelationKind = (typeof RELATION_KINDS)[number];

export interface RelationSettings {
  readonly kind: RelationKind;
  /** Object metadata UUID of the other endpoint. */
  readonly targetObjectId: string;
  /** For MANY_TO_MANY: object metadata UUID of the junction object (INV-9). */
  readonly junctionObjectId?: string | undefined;
  /**
   * ONE_TO_MANY only: the MANY_TO_ONE field this virtual reverse belongs to.
   * Set by side-effect expansion, which uses it to stay idempotent.
   */
  readonly reverseOfFieldId?: string | undefined;
}

/**
 * One SELECT / MULTI_SELECT option.
 *
 * `id` is the physical enum label and never changes. `label` and `color` are
 * presentation only, so editing them is a metadata write with zero DDL
 * (field-types.md, amended 2026-07-27).
 */
export interface SelectOption {
  readonly id: string;
  readonly label: string;
  readonly color?: string | undefined;
}

/** Hex or a token name; kept narrow because it ends up in a style attribute. */
export const SELECT_OPTION_COLOR_PATTERN = /^(#[0-9a-fA-F]{3,8}|[a-z][a-z0-9-]{0,31})$/;

export const selectOptionSchema = z.strictObject({
  id: z.string().regex(SELECT_OPTION_ID_PATTERN),
  label: z.string().min(1).max(255),
  color: z.string().regex(SELECT_OPTION_COLOR_PATTERN).optional(),
});

export const selectOptionsSchema = z.array(selectOptionSchema);

/**
 * Per-field configuration the physical layout and the value schema depend on.
 * Stored split across two jsonb columns on `meta.field_metadata`: the select
 * options in `options`, everything else in `settings`.
 */
export interface FieldTypeOptions {
  /** Allowed options for SELECT / MULTI_SELECT; stored values are their ids. */
  readonly selectOptions?: readonly SelectOption[] | undefined;
  readonly relation?: RelationSettings | undefined;
  /** CURRENCY: ISO-4217 code stored when the writer supplies none. */
  readonly defaultCurrencyCode?: string | undefined;
  /** PHONE: region used to canonicalise national numbers. */
  readonly phoneDefaultRegion?: string | undefined;
  /** Requests a unique index on the value column (built in phase B). */
  readonly isUnique?: boolean | undefined;
  /**
   * Marks this field as the object's LABEL: the one line that stands for a record
   * in a relation chip, a search hit or a picker. Only TEXT-family fields may be
   * flagged; see `label-field.ts` for the full resolution rule and its fallbacks.
   */
  readonly isLabel?: boolean | undefined;
  /**
   * Declares this field an IDENTIFIER in a namespace (identity-resolution.md 1.2).
   * Its values become matching keys, so two records holding the same normalized
   * value in the same namespace are the same person.
   *
   * A namespace UUID rather than its key: the namespace decides whether two values
   * are comparable at all, and INV-7 forbids keying anything on a mutable name - a
   * renamed namespace would otherwise silently change what matches what.
   *
   * Only TEXT, EMAIL, PHONE and URL may carry it (`IDENTITY_CAPABLE_FIELD_TYPES`
   * in `identity.ts` says why the other eight are refused).
   */
  readonly identity?: { readonly namespaceId: string } | undefined;
  /**
   * FILES: how many attachments one cell may hold (`file-attachments.md` D10, D21).
   * Defaults to `FILES_FIELD_DEFAULT_MAX`, capped at `FILES_FIELD_MAX`, and refused
   * outright on any other type by `metadata/validate.ts`.
   */
  readonly maxFiles?: number | undefined;
  /**
   * TEXT: this field holds PARAGRAPHS, not a line (`inbound-intake.md` D18).
   *
   * A presentation setting and nothing else - the column stays `text`, the value schema
   * stays a string, and no filter or import behaves differently. What it changes is the
   * shape of the two components that face the value: the display keeps the line breaks
   * the writer typed instead of collapsing them, and the input is a textarea rather than
   * a one-line box. Refused outright on any other type by `metadata/validate.ts`, the
   * `maxFiles` rule: a setting that is stored and then silently ignored is worse than one
   * that is refused with the field's name in the message.
   */
  readonly multiline?: boolean | undefined;
  /**
   * DATE: this date is a CHECKPOINT with a deadline of its own (`phase-b-coach.md` D1).
   *
   * A checklist in this product is a group of date fields, never a row of checkboxes: a
   * tick is a day, a day can start a clock and a day keeps a history. This setting is what
   * turns the day into a deadline. `baseFieldId` names the DATE field on the SAME object
   * the offset counts from (`contract_start_date`), and `null` means the offset counts
   * from the checkpoint named by `blockedByFieldId` instead - which is how the 48-hour
   * check-in counts from the onboarding call and reads "not yet possible" until that call
   * has a date.
   *
   * The deadline itself is computed by `shared/checklist.ts` and nowhere else, so the
   * browser and the engine cannot disagree about what "3 days late" means. Refused on any
   * other type by `metadata/validate.ts`, the `maxFiles` rule.
   */
  readonly checkpoint?: CheckpointSettings | undefined;
  /**
   * ANY TYPE: the system writes this field, a person does not (`phase-b-coach.md` D2).
   *
   * The record layer refuses a MANUAL write to it with `invalid_state`, and that refusal
   * is the point: a human write on a computed field HUMAN-LOCKS it (INV-5), and a locked
   * field is one the engine that owns it may never write again. So the field would freeze
   * at the value somebody typed while the rules behind it went on being true. The
   * judgement a person does have belongs in a sibling field with a date of its own.
   */
  readonly computed?: boolean | undefined;
  /**
   * ANY TYPE: this field is one of the object's KEY FACTS, and the number is its place in
   * the record header (1 to `KEY_FACT_LIMIT`; handbook DESIGN.md G2 and H2, register A6-13).
   *
   * The header shows the key facts a record has FILLED, in this order, and the field rows
   * under the header leave them out, so a value is said once. The list is CONFIGURATION:
   * the OFMOS seed names it per object (`seed/model.ts` `keyFacts`), the platform reads the
   * number and knows no object. A field without it is never a header fact, which is what
   * replaced the old rule "the first six fields by position".
   */
  readonly keyFact?: number | undefined;
}

/** The record header holds at most six facts (DESIGN.md G2: "at most six values"). */
export const KEY_FACT_LIMIT = 6;

/**
 * D1's three numbers, and the reason the base may be null.
 *
 * `blockedByFieldId` carries TWO meanings at once and they are the same meaning: the
 * checkpoint it names is what this deadline counts from when there is no base, and it is
 * also what makes this row "not yet possible" while it is empty. A checkpoint that
 * counted from one field and waited on another would be two settings; nothing has asked
 * for that, and the seeded eight do not need it.
 */
export interface CheckpointSettings {
  readonly baseFieldId: string | null;
  readonly offsetDays: number;
  readonly blockedByFieldId?: string | undefined;
  /**
   * A LATER DATE THAT MAKES THIS STEP UNNECESSARY (fulfillment round 2, finding F6).
   *
   * Names a DATE field on the SAME object. When that field carries a date and this one
   * does not, the step is `waived`: neither open nor late, never the next step, never an
   * overdue finding. Ben's rule (requirements E5): whoever already had the onboarding call
   * is never sent back to an earlier step, so the intake and the two gameplan steps of a
   * client who joined before the intake form existed stop reading "26 days late" forever.
   * A date on this step itself still wins: done is done.
   */
  readonly waivedByFieldId?: string | undefined;
}

/**
 * The offset's bound, in days, either way.
 *
 * A year is longer than every OFMOS contract and a checkpoint further out than that is a
 * date somebody typed by accident. Negative offsets are legal for the same reason
 * `today_plus_days` allows them (`workflow.ts`): "two days before the call" is as real a
 * deadline as two days after it.
 */
export const CHECKPOINT_OFFSET_MAX_DAYS = 365;

/**
 * ONE SCHEMA, TWO CALLERS, on the `identityFieldSettingsSchema` precedent: the API input
 * schema (`http/metadata-operations.ts`) and the store's read schema
 * (`db/metadata-store/read.ts`) both name it, so the shape a caller may send and the shape
 * that survives a metadata reload cannot drift apart.
 *
 * KEY ORDER IS LOAD-BEARING here as everywhere else in a settings blob: the seed emits
 * these keys in this order and `metadata/diff.ts` compares with `JSON.stringify`.
 */
export const checkpointFieldSettingsSchema = z.strictObject({
  baseFieldId: z.uuid().nullable(),
  offsetDays: z.number().int().min(-CHECKPOINT_OFFSET_MAX_DAYS).max(CHECKPOINT_OFFSET_MAX_DAYS),
  blockedByFieldId: z.uuid().optional(),
  waivedByFieldId: z.uuid().optional(),
});

// --- per-column text format (csv-import-export.md D7) -----------------------

/**
 * FORMAT IS DECLARED PER COLUMN, NEVER GUESSED PER ROW (D7).
 *
 * The two commonest German conventions are exactly the two an inference would get
 * wrong - `31.07.2026` against `07/31/2026`, and `1.234,56` against `1,234.56` - and
 * a file whose first hundred rows all have a day above twelve infers correctly while
 * the same file starting in January does not. So the declaration travels with every
 * `fromText` call and the entry never sniffs.
 *
 * It is a COMPLETE object rather than a bag of optionals, so a caller cannot reach
 * coercion having declared nothing. `columnFormat()` builds one from the defaults,
 * which is what the wizard seeds from the first fifty non-empty values.
 */
export const COLUMN_DATE_FORMATS = ['ISO', 'DD.MM.YYYY', 'MM/DD/YYYY'] as const;
export type ColumnDateFormat = (typeof COLUMN_DATE_FORMATS)[number];

export const COLUMN_DECIMAL_SEPARATORS = ['.', ','] as const;
export type ColumnDecimalSeparator = (typeof COLUMN_DECIMAL_SEPARATORS)[number];

/** `''` means the file writes no thousands separator at all, which is the common case. */
export const COLUMN_THOUSANDS_SEPARATORS = ['', '.', ',', ' '] as const;
export type ColumnThousandsSeparator = (typeof COLUMN_THOUSANDS_SEPARATORS)[number];

/**
 * D7's default truthy vocabulary, split into the two lists a parser actually needs.
 * `x` has no falsy counterpart on purpose: a crossed cell means yes, and an uncrossed
 * one is blank, which never reaches coercion at all (D17).
 */
export const DEFAULT_TRUE_TOKENS = ['true', '1', 'yes', 'ja', 'x'] as const;
export const DEFAULT_FALSE_TOKENS = ['false', '0', 'no', 'nein'] as const;

/** The separator inside ONE MULTI_SELECT cell (D7), not the file's delimiter. */
export const DEFAULT_MULTI_SELECT_SEPARATOR = ';';

/**
 * The separator between KEYS inside one relation-link cell (D7, D9 as amended for the
 * two relation kinds that own no column), not the file's delimiter.
 *
 * ITS OWN MEMBER RATHER THAN `multiSelectSeparator`, even though both default to `;` and
 * both split one cell into several values. A MULTI_SELECT cell holds option labels the
 * operator can see in the field editor; a link cell holds keys into ANOTHER object, and
 * one file can easily write the two apart - `a;b` for statuses and `a, b` for company
 * domains - because the two columns came out of different systems. Sharing the member
 * would make the two impossible to declare separately, which is exactly the "declared per
 * column, never guessed" property D7 exists for.
 */
export const DEFAULT_RELATION_SEPARATOR = ';';

/**
 * `Z`, or a fixed `+HH:MM` / `-HH:MM`. Applied ONLY to a DATETIME cell that carries no
 * zone of its own; a cell that carries one keeps it.
 *
 * D7's table declares a date FORMAT for DATETIME and stops there, but a zoneless
 * `31.07.2026 14:30` is not an instant and `DATETIME`'s value schema requires an offset
 * (`z.iso.datetime({ offset: true })`). Guessing the instance's zone is the inference D7
 * forbids, so the zone is DECLARED like every other ambiguity in that table - which is
 * also what D7's own HubSpot citation describes ("confirms date format, time zone and
 * number format on its final step"). A fixed offset cannot express DST; a column whose
 * values straddle a DST boundary must be exported as ISO with real offsets instead.
 */
export const DEFAULT_DATETIME_OFFSET = 'Z';

export const DATETIME_OFFSET_PATTERN = /^(Z|[+-]([01]\d|2[0-3]):[0-5]\d)$/;

export interface ColumnFormat {
  /** DATE and DATETIME: how the date part of a cell is written. */
  readonly dateFormat: ColumnDateFormat;
  /** NUMBER and CURRENCY. */
  readonly decimalSeparator: ColumnDecimalSeparator;
  readonly thousandsSeparator: ColumnThousandsSeparator;
  /** DATETIME: the zone for cells that carry none. */
  readonly datetimeOffset: string;
  readonly trueTokens: readonly string[];
  readonly falseTokens: readonly string[];
  readonly multiSelectSeparator: string;
  /** ONE_TO_MANY and MANY_TO_MANY: the separator between keys in one link cell. */
  readonly relationSeparator: string;
  /** CURRENCY: null means "the field's `defaultCurrencyCode`" (D7). */
  readonly currencyCode: string | null;
  /** PHONE: null means "the field's `phoneDefaultRegion`" (D7). */
  readonly phoneRegion: string | null;
}

export const DEFAULT_COLUMN_FORMAT: ColumnFormat = {
  dateFormat: 'ISO',
  decimalSeparator: '.',
  thousandsSeparator: '',
  datetimeOffset: DEFAULT_DATETIME_OFFSET,
  trueTokens: DEFAULT_TRUE_TOKENS,
  falseTokens: DEFAULT_FALSE_TOKENS,
  multiSelectSeparator: DEFAULT_MULTI_SELECT_SEPARATOR,
  relationSeparator: DEFAULT_RELATION_SEPARATOR,
  currencyCode: null,
  phoneRegion: null,
};

/**
 * A COMPLETE format from whatever a caller holds, and this is what makes a member added
 * later safe to read.
 *
 * `meta.import_run.formats` is jsonb read back without a zod parse (`db/csv-store/runs.ts`),
 * so a format stored before `relationSeparator` existed simply has no such key. Spreading
 * the stored object over the defaults fills it, because JSON cannot store an explicit
 * `undefined` that would override one. Every reader of a STORED format goes through here
 * rather than through `?? DEFAULT_COLUMN_FORMAT`, which would have given an old row's
 * declared date format away to keep its separator.
 */
export function columnFormat(overrides: Partial<ColumnFormat> = {}): ColumnFormat {
  return { ...DEFAULT_COLUMN_FORMAT, ...overrides };
}

// --- the result of reading one cell (D8) ------------------------------------

/**
 * Why these are CODES and not sentences: they land in the `_error` column of the error
 * artifact (D13), `;`-separated and in UPPER_SNAKE_CASE, and a person re-importing a
 * fixed file greps them.
 */
export const TEXT_PARSE_CODES = [
  /** Nothing but whitespace. The importer never writes one (D17); the code exists so a caller that does reach coercion is told why. */
  'EMPTY',
  'NOT_A_NUMBER',
  'NOT_A_DATE',
  'NOT_A_DATETIME',
  'NOT_A_BOOLEAN',
  'NOT_A_UUID',
  'OUT_OF_RANGE',
  /** More decimal places than the storage keeps. Truncating money is not a rounding error. */
  'TOO_PRECISE',
  'NO_MATCHING_OPTION',
  'AMBIGUOUS_OPTION',
  'NO_OPTIONS_CONFIGURED',
  'RELATION_NEEDS_LOOKUP',
  'RELATION_NOT_WRITABLE',
  'INVALID_EMAIL',
  'INVALID_URL',
  'INVALID_CURRENCY_CODE',
  /**
   * FILES (`file-attachments.md` D12). A CSV can carry a filename but not the file, so
   * `fromText` ALWAYS refuses with this - the only entry that never depends on the cell.
   * It never fires in practice, because the mapping screen refuses first with the sentence
   * section 12 writes; it exists so a caller that reaches coercion is told why.
   */
  'NOT_IMPORTABLE',
] as const;

export type TextParseCode = (typeof TEXT_PARSE_CODES)[number];

/**
 * A TYPED REFUSAL RATHER THAN A THROW (D8 property 3): the importer collects thousands
 * of failures instead of aborting on the first one.
 */
export type TextParse =
  | { readonly ok: true; readonly value: unknown }
  | { readonly ok: false; readonly code: TextParseCode; readonly message: string };

export interface FieldTypeDef {
  /**
   * The physical columns this field owns, in DDL order. Empty for relation kinds
   * that store nothing (ONE_TO_MANY is a virtual reverse, MANY_TO_MANY lives in
   * a junction object). A function rather than a constant because RELATION and
   * the enum types depend on per-field configuration.
   */
  readonly physicalColumns: (options?: FieldTypeOptions) => readonly PhysicalColumnSpec[];
  /**
   * Validator for one stored value of this field type. A factory rather than a
   * constant because SELECT and friends narrow against per-field options.
   */
  readonly valueSchema: (options?: FieldTypeOptions) => z.ZodType;
  /**
   * ONE CELL OF TEXT -> ONE STORED VALUE (csv-import-export.md D8).
   *
   * A registry MEMBER rather than a coercion module switching on `FieldType`, because
   * that switch is what INV-12 exists to prevent: a thirteenth field type would compile
   * and then quietly not import. It is also not a change to `valueSchema`, which
   * `toFieldWrite` hands every writer in the system - coercing it would let an HTTP
   * client, a workflow step and Oliver write `"42"` into a NUMBER.
   *
   * FOUR PROPERTIES MAKE IT SAFE:
   *  1. It never bypasses validation. Its output goes to `toFieldWrite`, which parses it
   *     with `valueSchema` exactly as for every other caller. Coercion is additive in
   *     front of the gate, never a second gate.
   *  2. The write path never calls it. One registry, two doors.
   *  3. It returns a typed refusal instead of throwing.
   *  4. PHONE's is the identity function: `toFieldWrite` already accepts a plain string
   *     and derives the canonical form itself, and we do not reimplement what is correct.
   *
   * `raw` is one parsed cell. Every entry trims surrounding whitespace before deciding,
   * because a trailing space in a spreadsheet cell is an artefact of typing rather than
   * data. Injection armor is stripped BEFORE this (`stripCsvArmor`, D36), so a leading
   * zero-width joiner never reaches an entry.
   */
  readonly fromText: (raw: string, options: FieldTypeOptions, format: ColumnFormat) => TextParse;
  /**
   * ONE STORED VALUE -> ONE CELL OF TEXT (csv-import-export.md D37).
   *
   * Declared beside `fromText` on one interface so the round trip is a property of the
   * registry rather than a coincidence between two modules. `value` is the LOGICAL value
   * the record layer reads out (`toLogicalValue`), and it is never null here: the ABSENT
   * value is answered by `fieldValueToText` with an empty cell, which re-imports as
   * `skipped_blank` (D17), so the round trip holds there without twelve null branches.
   *
   * IT RETURNS THE ONE COLUMN THE LAYOUT EMITS, IMPORTABLE UNLESS THE LAYOUT SAYS OTHERWISE
   * (widened by `file-attachments.md` D12: FILES is the first type whose only column is
   * declared NON-importable, through the `<name> files` header suffix). CURRENCY, PHONE and
   * RELATION each export a
   * SECOND column - the ISO code, the number as entered, the target's label (D37) - and
   * those are export LAYOUT, built from `physicalColumns` and, for the relation label,
   * from a batched lookup. `toText` is the one column a re-import reads.
   *
   * It THROWS on a value shape it cannot serialize rather than emitting something
   * plausible: an export is a job with a failure state, and a file that looks complete
   * is the exact failure D35 refuses.
   */
  readonly toText: (value: unknown, options: FieldTypeOptions) => string;
  /** Operators the UI may offer and both filter backends must implement. */
  readonly filterOperators: readonly FilterOperator[];
  /** How values of this type are read and compared by both filter backends. */
  readonly filterFamily: FilterFamily;
  /**
   * WHETHER THIS TYPE MAY BE A GROUP DIMENSION (group-by-aggregation.md D11, D14).
   *
   * A registry MEMBER rather than a `switch (fieldType)` inside a new group module, for
   * the same reason `fromText` is one: that switch is precisely what INV-12 exists to
   * prevent, and a thirteenth field type would compile and then quietly not be groupable.
   * It also gives the field picker its data source for free - `groupable !== 'none'` is
   * the list of groupable TYPES, with no second table to drift.
   *
   * RELATION declares `'value'` for the TYPE, and the KIND is refused one layer down
   * where it already is: ONE_TO_MANY and MANY_TO_MANY own no column and `filterRefsOf`
   * throws `not_supported` for them before any grouping code runs.
   *
   * The picker filters SYSTEM fields on top of this, because `created_at`, `updated_at`
   * and `deleted_at` are all DATETIME and a menu offering "group by deleted_at" is not
   * something anybody meant to ship. That is a UI filter, not a server rule: grouping by
   * `created_at` through the API is legitimate and is the one date bucket a user would
   * actually ask for (D14).
   */
  readonly groupable: GroupableKind;
  /**
   * WHICH AGGREGATES MAY BE APPLIED TO A FIELD OF THIS TYPE (D12, D14).
   *
   * Every type carries the universal three - `countValues`, `countEmpty`, `countUnique` -
   * because they ask about presence rather than about arithmetic. `sum` and `avg` are
   * numeric only; `min` and `max` add the two temporal types; `countTrue` and
   * `countFalse` are BOOLEAN's alone.
   *
   * `count` is in NO list, deliberately: it is the group's own row count and names no
   * field (see `AGGREGATE_OPS`).
   */
  readonly aggregates: readonly AggregateOp[];
  /**
   * Whether this type contributes to the record's full-text search vector. The
   * write path reads it to decide which columns to concatenate, so a new field
   * type becomes searchable by setting a flag here and nowhere else (INV-12).
   */
  readonly searchable: boolean;
}

// --- shared operator sets ---------------------------------------------------

const TEXTUAL_OPERATORS = [
  'eq',
  'neq',
  'contains',
  'notContains',
  'startsWith',
  'endsWith',
  'in',
  'notIn',
  'isEmpty',
  'isNotEmpty',
] as const satisfies readonly FilterOperator[];

const NUMERIC_OPERATORS = [
  'eq',
  'neq',
  'lt',
  'lte',
  'gt',
  'gte',
  'between',
  'isEmpty',
  'isNotEmpty',
] as const satisfies readonly FilterOperator[];

/**
 * `gte` and `lt` are here for the HALF-OPEN BOUND a date bucket's "show more" needs
 * (`docs/decisions/group-by-aggregation.md` D19), and their absence was the one thing
 * blocking it: `before` is `<` and `after` is `>`, so the UPPER half-open bound already
 * existed while the LOWER one did not, and `between` is inclusive on BOTH ends - which
 * would return a row sitting exactly on the next bucket's start in two adjacent groups'
 * row lists while `date_trunc` counted it in exactly one.
 *
 * `lt` therefore duplicates `before`'s compiled semantics exactly, and is declared anyway:
 * a half-open pair spelled `and(gte start, before end)` reads as two different kinds of
 * comparison, and D19's table names both operators. `gte` has no temporal twin at all.
 */
const TEMPORAL_OPERATORS = [
  'eq',
  'neq',
  'lt',
  'gte',
  'before',
  'after',
  'between',
  'isEmpty',
  'isNotEmpty',
] as const satisfies readonly FilterOperator[];

const ENUMERABLE_OPERATORS = [
  'eq',
  'neq',
  'in',
  'notIn',
  'isEmpty',
  'isNotEmpty',
] as const satisfies readonly FilterOperator[];

const BOOLEAN_OPERATORS = [
  'eq',
  'neq',
  'isEmpty',
  'isNotEmpty',
] as const satisfies readonly FilterOperator[];

const MULTI_SELECT_OPERATORS = [
  'contains',
  'containsAny',
  'isEmpty',
  'isNotEmpty',
] as const satisfies readonly FilterOperator[];

/**
 * FILES: presence and nothing else (`file-attachments.md` D10). Nothing else has an honest
 * SQL implementation over a jsonb array of opaque references - `contains` would have to
 * mean "holds this file id", which nobody types, and a textual operator over a label is
 * filename search, a named non-goal (section 11).
 */
const FILES_OPERATORS = ['isEmpty', 'isNotEmpty'] as const satisfies readonly FilterOperator[];

// --- shared aggregate sets (D12) --------------------------------------------

/**
 * TYPE-AGNOSTIC: all three ask about PRESENCE, not about arithmetic. AMENDED by
 * `file-attachments.md` D10 - `countUnique` is meaningless on FILES, whose value is a SET,
 * so the set below is the universal TWO plus a third that needs values to compare. RELATION gets them too - a
 * deviation from Twenty, which gives relations `count` alone, and it is affordable
 * because ours is a plain uuid column rather than a join, so "how many distinct
 * companies" is one expression (D12, section 15 row 5).
 */
const UNIVERSAL_AGGREGATES = [
  'countValues',
  'countEmpty',
  'countUnique',
] as const satisfies readonly AggregateOp[];

/**
 * FILES (`file-attachments.md` D10): `countValues` and `countEmpty` and no third.
 * "Unique attachments: 4" is a count of distinct SETS, which is a number no screen can
 * label, so it is excluded here rather than explained in a footer.
 */
const FILES_AGGREGATES = ['countValues', 'countEmpty'] as const satisfies readonly AggregateOp[];

const NUMERIC_AGGREGATES = [
  ...UNIVERSAL_AGGREGATES,
  'sum',
  'avg',
  'min',
  'max',
] as const satisfies readonly AggregateOp[];

/** No `sum` and no `avg`: the mean of two dates is not a date anybody asked for. */
const TEMPORAL_AGGREGATES = [
  ...UNIVERSAL_AGGREGATES,
  'min',
  'max',
] as const satisfies readonly AggregateOp[];

const BOOLEAN_AGGREGATES = [
  ...UNIVERSAL_AGGREGATES,
  'countTrue',
  'countFalse',
] as const satisfies readonly AggregateOp[];

// --- helpers ---------------------------------------------------------------

/**
 * A stored SELECT value is an option ID, not a label. Constrained to the
 * configured ids when they are known; without options the registry only asserts
 * the id FORMAT, because it must not pretend to know which ids a field has, but
 * it does know that a label could never be a legal stored value.
 */
function selectValueSchema(options?: FieldTypeOptions): z.ZodType {
  const allowed = options?.selectOptions;
  if (allowed !== undefined && allowed.length > 0) {
    return z.literal(allowed.map((option) => option.id));
  }
  return z.string().regex(SELECT_OPTION_ID_PATTERN);
}

/** Option ids in declaration order; what the physical enum type contains. */
export function selectOptionIds(options: readonly SelectOption[] | null): readonly string[] {
  return (options ?? []).map((option) => option.id);
}

export function findSelectOption(
  options: readonly SelectOption[] | null,
  id: string,
): SelectOption | undefined {
  return (options ?? []).find((option) => option.id === id);
}

const singleColumn = (sql: string): readonly PhysicalColumnSpec[] => [
  { suffix: COLUMN_SUFFIXES.value, type: staticType(sql), role: 'value' },
];

/** Micros keep money exact; 2^53 micros is still ~9 billion units of currency. */
export const CURRENCY_MICROS_PER_UNIT = 1_000_000;
export const DEFAULT_CURRENCY_CODE = 'EUR';
export const DEFAULT_PHONE_REGION = 'DE';

export const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;

export function isValidCurrencyCode(code: string): boolean {
  return CURRENCY_CODE_PATTERN.test(code);
}

/**
 * The CURRENCY default is the ONE per-field setting that becomes part of a DDL
 * statement (`default 'EUR'`), so it is narrowed here, at the point where the SQL
 * fragment is built, and not only wherever it happens to be validated on the way
 * in. Anything that is not a bare ISO-4217 code falls back to the default rather
 * than being interpolated: a settings blob edited by hand in psql, or a future
 * writer that forgets to validate, must not be able to reach the schema manager
 * with a string of its choosing.
 */
function currencyDefaultSql(code: string | undefined): string {
  const safe = code !== undefined && isValidCurrencyCode(code) ? code : DEFAULT_CURRENCY_CODE;
  return `'${safe}'`;
}

/**
 * PHONE value: the canonical E.164 form plus the input it came from. `canonical`
 * is null when the input could not be parsed - such input is stored raw and
 * flagged, never silently dropped (field-types.md decision 3).
 */
export const phoneValueSchema = z.strictObject({
  canonical: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/)
    .nullable(),
  original: z.string().min(1),
});
export type PhoneValue = z.infer<typeof phoneValueSchema>;

/**
 * ONE ATTACHMENT, AS STORED (`file-attachments.md` D7, D10).
 *
 * `.strictObject` so nothing else can be smuggled into a cell - the client sends
 * `{fileId, label}` and the SERVER replaces the array with this enriched form, so
 * `byteCount` and `contentType` in a cell are always the server's own numbers and a read
 * needs no join. That denormalisation is safe because a file is IMMUTABLE after `stored`:
 * no route changes its bytes, its size or its type, and its label is fixed at reserve time.
 *
 * THE KEY ORDER IS LOAD-BEARING. `sameLogicalValue` is `JSON.stringify` equality
 * (`records/values.ts`), so an enrichment that built these four keys in a different order
 * would make re-saving an untouched form emit an `updated` event with a diff nobody made.
 * Written once here, and the write path builds items through this order.
 */
export const recordFileItemSchema = z.strictObject({
  fileId: z.uuid(),
  label: z.string().min(1).max(255),
  byteCount: z.number().int().positive(),
  contentType: z.string().min(1),
});

/** CURRENCY value: an exact integer amount in micros plus its ISO-4217 code. */
export const currencyValueSchema = z.strictObject({
  amountMicros: z.int(),
  currencyCode: z.string().regex(/^[A-Z]{3}$/),
});
export type CurrencyValue = z.infer<typeof currencyValueSchema>;

// --- reading and writing text (D8, D37) -------------------------------------

const parsedValue = (value: unknown): TextParse => ({ ok: true, value });

const refuse = (code: TextParseCode, message: string): TextParse => ({ ok: false, code, message });

const BLANK = refuse('EMPTY', 'the cell is empty');

type TextParser = (raw: string, options: FieldTypeOptions, format: ColumnFormat) => TextParse;

/** Trim once and refuse blank once, so all twelve entries obey the same two rules. */
function nonBlank(parse: TextParser): TextParser {
  return (raw, options, format) => {
    const trimmed = raw.trim();
    return trimmed === '' ? BLANK : parse(trimmed, options, format);
  };
}

/** `^[+-]?(digits[.digits] | .digits)$` - no exponent, no separators left. */
const PLAIN_DECIMAL_PATTERN = /^([+-]?)(\d+)(?:\.(\d+))?$|^([+-]?)\.(\d+)$/;

interface DecimalParts {
  readonly negative: boolean;
  readonly whole: string;
  readonly fraction: string;
}

/**
 * The declared separators applied in the one order that cannot misread a German file:
 * remove the thousands separator first, then accept exactly one decimal separator. With
 * a decimal comma declared, a dot that survived the first step is not a decimal point -
 * it is a character this column's grammar has no room for, so the cell is refused rather
 * than reinterpreted.
 */
function splitDecimal(raw: string, format: ColumnFormat): DecimalParts | null {
  let body = raw;
  const { thousandsSeparator, decimalSeparator } = format;
  if (thousandsSeparator !== '' && thousandsSeparator !== decimalSeparator) {
    body = body.split(thousandsSeparator).join('');
  }
  if (decimalSeparator !== '.') {
    if (body.includes('.')) return null;
    const first = body.indexOf(decimalSeparator);
    if (first !== -1) {
      if (body.indexOf(decimalSeparator, first + 1) !== -1) return null;
      body = `${body.slice(0, first)}.${body.slice(first + 1)}`;
    }
  }
  const match = body.match(PLAIN_DECIMAL_PATTERN);
  if (match === null) return null;
  const sign = match[1] ?? match[4] ?? '';
  return {
    negative: sign === '-',
    whole: match[2] ?? '0',
    fraction: match[3] ?? match[5] ?? '',
  };
}

function numberFromText(raw: string, format: ColumnFormat): TextParse {
  const parts = splitDecimal(raw, format);
  if (parts === null) {
    return refuse(
      'NOT_A_NUMBER',
      `"${raw}" is not a number with "${format.decimalSeparator}" as the decimal separator`,
    );
  }
  const value = Number(`${parts.negative ? '-' : ''}${parts.whole}.${parts.fraction || '0'}`);
  if (!Number.isFinite(value)) return refuse('OUT_OF_RANGE', `"${raw}" is out of range`);
  return parsedValue(value);
}

/**
 * Micros through STRING arithmetic, never `amount * 1_000_000`: the multiplication is
 * what turns 2100.57 into 2100569999.9999998, and money is the one column where a
 * rounding artefact is a defect rather than a nuisance.
 */
function currencyFromText(raw: string, options: FieldTypeOptions, format: ColumnFormat): TextParse {
  const code = format.currencyCode ?? options.defaultCurrencyCode ?? DEFAULT_CURRENCY_CODE;
  if (!isValidCurrencyCode(code)) {
    return refuse('INVALID_CURRENCY_CODE', `"${code}" is not an ISO-4217 code`);
  }
  const parts = splitDecimal(raw, format);
  if (parts === null) {
    return refuse(
      'NOT_A_NUMBER',
      `"${raw}" is not an amount with "${format.decimalSeparator}" as the decimal separator`,
    );
  }
  if (parts.fraction.length > 6) {
    return refuse('TOO_PRECISE', `"${raw}" has more than six decimal places and cannot be stored`);
  }
  const micros =
    BigInt(parts.whole) * BigInt(CURRENCY_MICROS_PER_UNIT) +
    BigInt(parts.fraction.padEnd(6, '0') || '0');
  const signed = parts.negative ? -micros : micros;
  if (signed > BigInt(Number.MAX_SAFE_INTEGER) || signed < BigInt(Number.MIN_SAFE_INTEGER)) {
    return refuse('OUT_OF_RANGE', `"${raw}" is larger than this column can hold exactly`);
  }
  return parsedValue({ amountMicros: Number(signed), currencyCode: code });
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const GERMAN_DATE_PATTERN = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const US_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const pad = (value: string, width: number): string => value.padStart(width, '0');

/**
 * The declared format decides the ORDER, and nothing else does. Day and month may be
 * written unpadded, because a hand-typed German date usually is; the year may not,
 * because a two-digit year is an ambiguity no declaration in D7's table resolves.
 *
 * Calendar validity is left to `z.iso.date()`, which refuses `2026-02-30`.
 */
function isoDateFromText(raw: string, format: ColumnDateFormat): string | null {
  switch (format) {
    case 'ISO': {
      const match = raw.match(ISO_DATE_PATTERN);
      return match === null ? null : raw;
    }
    case 'DD.MM.YYYY': {
      const match = raw.match(GERMAN_DATE_PATTERN);
      if (match === null) return null;
      return `${match[3]}-${pad(match[2] ?? '', 2)}-${pad(match[1] ?? '', 2)}`;
    }
    case 'MM/DD/YYYY': {
      const match = raw.match(US_DATE_PATTERN);
      if (match === null) return null;
      return `${match[3]}-${pad(match[1] ?? '', 2)}-${pad(match[2] ?? '', 2)}`;
    }
    default:
      return null;
  }
}

const isoDateSchema = z.iso.date();
const isoDateTimeSchema = z.iso.datetime({ offset: true });

function dateFromText(raw: string, format: ColumnFormat): TextParse {
  const iso = isoDateFromText(raw, format.dateFormat);
  if (iso === null || !isoDateSchema.safeParse(iso).success) {
    return refuse('NOT_A_DATE', `"${raw}" is not a date in ${format.dateFormat}`);
  }
  return parsedValue(iso);
}

const ZONE_SUFFIX_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/;
const TIME_PATTERN = /^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?(\.\d{1,3})?$/;

/**
 * `<date>[ T]<time>[zone]`, where the date part follows the column's declared format and
 * a missing zone is filled from `format.datetimeOffset`. An explicit zone in the cell
 * always wins: a file that already carries offsets is a machine export and knows better
 * than any declaration made about it.
 */
function datetimeFromText(raw: string, format: ColumnFormat): TextParse {
  const refusal = refuse(
    'NOT_A_DATETIME',
    `"${raw}" is not a date and time in ${format.dateFormat}`,
  );
  const zoneMatch = raw.match(ZONE_SUFFIX_PATTERN);
  const zoneRaw = zoneMatch?.[1];
  const zone =
    zoneRaw === undefined
      ? format.datetimeOffset
      : zoneRaw === 'Z'
        ? 'Z'
        : zoneRaw.includes(':')
          ? zoneRaw
          : `${zoneRaw.slice(0, 3)}:${zoneRaw.slice(3)}`;
  if (!DATETIME_OFFSET_PATTERN.test(zone)) return refusal;

  const body = (zoneRaw === undefined ? raw : raw.slice(0, raw.length - zoneRaw.length)).trim();
  const separator = body.search(/[T ]/);
  const datePart = separator === -1 ? body : body.slice(0, separator);
  const timePart = separator === -1 ? '00:00:00' : body.slice(separator + 1).trim();

  const isoDate = isoDateFromText(datePart, format.dateFormat);
  if (isoDate === null) return refusal;
  const time = timePart.match(TIME_PATTERN);
  if (time === null) return refusal;
  const hour = Number(time[1]);
  if (hour > 23) return refusal;

  const composed = `${isoDate}T${pad(String(hour), 2)}:${time[2]}:${time[3] ?? '00'}${
    time[4] ?? ''
  }${zone}`;
  if (!isoDateTimeSchema.safeParse(composed).success) return refusal;
  return parsedValue(composed);
}

function booleanFromText(raw: string, format: ColumnFormat): TextParse {
  const token = raw.toLowerCase();
  const matches = (tokens: readonly string[]): boolean =>
    tokens.some((candidate) => candidate.toLowerCase() === token);
  if (matches(format.trueTokens)) return parsedValue(true);
  if (matches(format.falseTokens)) return parsedValue(false);
  return refuse(
    'NOT_A_BOOLEAN',
    `"${raw}" is not one of ${[...format.trueTokens, ...format.falseTokens].join(', ')}`,
  );
}

/**
 * SELECT BY LABEL, THEN BY ID (D9). The stored value is an option id and never a label,
 * so label resolution is new code and it belongs here rather than in the write path.
 *
 * Two options whose labels differ only in case make the cell an ERROR, not a coin flip.
 * An unmatched label is a value error too: no import creates an option, because that is
 * a PostgreSQL enum change and therefore a migration (D10).
 */
function selectOptionFromText(raw: string, options: FieldTypeOptions): TextParse {
  const available = options.selectOptions ?? [];
  if (available.length === 0) {
    return refuse('NO_OPTIONS_CONFIGURED', 'this field has no options to match against');
  }
  const lower = raw.toLowerCase();
  const byLabel = available.filter((option) => option.label.toLowerCase() === lower);
  if (byLabel.length > 1) {
    return refuse(
      'AMBIGUOUS_OPTION',
      `"${raw}" matches ${byLabel.length} options whose labels differ only in case`,
    );
  }
  const single = byLabel[0];
  if (single !== undefined) return parsedValue(single.id);
  const byId = available.find((option) => option.id === raw);
  if (byId !== undefined) return parsedValue(byId.id);
  return refuse('NO_MATCHING_OPTION', `"${raw}" is not one of this field's options`);
}

function multiSelectFromText(
  raw: string,
  options: FieldTypeOptions,
  format: ColumnFormat,
): TextParse {
  const parts = raw
    .split(format.multiSelectSeparator)
    .map((part) => part.trim())
    .filter((part) => part !== '');
  if (parts.length === 0) return BLANK;
  const ids: unknown[] = [];
  for (const part of parts) {
    const resolved = selectOptionFromText(part, options);
    if (!resolved.ok) return resolved;
    ids.push(resolved.value);
  }
  return parsedValue(ids);
}

const uuidSchema = z.uuid();
const emailSchema = z.email();
const urlSchema = z.url();

/**
 * RELATION IS A LOOKUP, NOT A STRING TRANSFORM (D9), so the addressing modes that need a
 * query - a target's unique or identity field - are resolved by the importer's batched
 * stage and never here. The one mode this entry can decide alone is a target UUID, and
 * anything else is refused by NAME so the caller cannot mistake the refusal for a value
 * error in the file.
 */
function relationFromText(raw: string, options: FieldTypeOptions): TextParse {
  const kind = options.relation?.kind;
  if (kind !== undefined && kind !== 'MANY_TO_ONE') {
    return refuse(
      'RELATION_NOT_WRITABLE',
      `a ${kind} relation owns no column and is not importable`,
    );
  }
  if (uuidSchema.safeParse(raw).success) return parsedValue(raw);
  return refuse(
    'RELATION_NEEDS_LOOKUP',
    `"${raw}" is not a record id; a relation addressed by a unique or identity value is resolved by the importer, not by the registry`,
  );
}

function requireString(value: unknown, fieldType: FieldType): string {
  if (typeof value !== 'string') {
    throw new TypeError(`${fieldType} value is not a string: ${JSON.stringify(value)}`);
  }
  return value;
}

/** DATE and DATETIME come back as `Date` from the driver and as ISO strings from the API. */
function isoTextOf(value: unknown, fieldType: 'DATE' | 'DATETIME'): string {
  if (value instanceof Date) {
    return fieldType === 'DATE' ? value.toISOString().slice(0, 10) : value.toISOString();
  }
  return requireString(value, fieldType);
}

/**
 * NEVER LOCALE-FORMATTED, even though import accepts German formatting (D37): an exported
 * `1.234,56` could not be re-imported without the reader knowing the declaration, so the
 * file would depend on out-of-band knowledge. Import is lenient because humans author
 * files; export is canonical because machines and re-imports read them.
 *
 * KNOWN EDGE: a magnitude at or beyond 1e21 renders in exponent notation, which
 * `fromText` refuses on the way back. Nothing in a CRM produces one and no guard is
 * added for it, but it is stated rather than discovered.
 */
function numberToText(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TypeError(`NUMBER value is not a finite number: ${JSON.stringify(value)}`);
  }
  return String(value);
}

function microsToDecimalText(micros: number): string {
  const negative = micros < 0;
  const absolute = Math.abs(micros);
  const whole = Math.trunc(absolute / CURRENCY_MICROS_PER_UNIT);
  const fraction = pad(String(absolute - whole * CURRENCY_MICROS_PER_UNIT), 6).replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${fraction === '' ? '' : `.${fraction}`}`;
}

/** The option LABEL (D37). A label the metadata no longer holds falls back to its id. */
function selectLabelOf(id: string, options: FieldTypeOptions): string {
  return findSelectOption(options.selectOptions ?? null, id)?.label ?? id;
}

// --- the registry ----------------------------------------------------------

export const FIELD_TYPE_REGISTRY = {
  TEXT: {
    physicalColumns: () => singleColumn('text'),
    valueSchema: () => z.string(),
    fromText: nonBlank((raw) => parsedValue(raw)),
    toText: (value) => requireString(value, 'TEXT'),
    filterOperators: TEXTUAL_OPERATORS,
    filterFamily: 'text',
    /**
     * Unbounded cardinality: a text column has as many values as it has rows. The board
     * already refuses free text because it "would invent columns from typos", and the
     * argument is stronger on a table, which has no closed column set to protect it.
     */
    groupable: 'none',
    aggregates: UNIVERSAL_AGGREGATES,
    searchable: true,
  },
  NUMBER: {
    physicalColumns: () => singleColumn('double precision'),
    valueSchema: () => z.number().finite(),
    fromText: nonBlank((raw, _options, format) => numberFromText(raw, format)),
    toText: (value) => numberToText(value),
    filterOperators: NUMERIC_OPERATORS,
    filterFamily: 'number',
    /** A raw number column is as wide as its row count; binning it is a different feature. */
    groupable: 'none',
    aggregates: NUMERIC_AGGREGATES,
    searchable: false,
  },
  BOOLEAN: {
    physicalColumns: () => singleColumn('boolean'),
    valueSchema: () => z.boolean(),
    fromText: nonBlank((raw, _options, format) => booleanFromText(raw, format)),
    toText: (value) => {
      if (typeof value !== 'boolean') {
        throw new TypeError(`BOOLEAN value is not a boolean: ${JSON.stringify(value)}`);
      }
      return value ? 'true' : 'false';
    },
    filterOperators: BOOLEAN_OPERATORS,
    filterFamily: 'boolean',
    /** Three groups at most, no-value included, and each one is an `eq` or an `isEmpty`. */
    groupable: 'value',
    aggregates: BOOLEAN_AGGREGATES,
    searchable: false,
  },
  DATE: {
    physicalColumns: () => singleColumn('date'),
    valueSchema: () => z.iso.date(),
    fromText: nonBlank((raw, _options, format) => dateFromText(raw, format)),
    toText: (value) => isoTextOf(value, 'DATE'),
    filterOperators: TEMPORAL_OPERATORS,
    filterFamily: 'date',
    /** Bucketed only (D13). A raw `date` column has a value per day of the calendar. */
    groupable: 'bucketed',
    aggregates: TEMPORAL_AGGREGATES,
    searchable: false,
  },
  DATETIME: {
    physicalColumns: () => singleColumn('timestamptz'),
    valueSchema: () => z.iso.datetime({ offset: true }),
    fromText: nonBlank((raw, _options, format) => datetimeFromText(raw, format)),
    toText: (value) => isoTextOf(value, 'DATETIME'),
    filterOperators: TEMPORAL_OPERATORS,
    filterFamily: 'datetime',
    /** Bucketed only, and the bucket's timezone is MANDATORY: a day in UTC-12 is not the same period as a day in UTC+3 (D13). */
    groupable: 'bucketed',
    aggregates: TEMPORAL_AGGREGATES,
    searchable: false,
  },

  /** One column of the field's own PostgreSQL enum type. */
  SELECT: {
    physicalColumns: () => [
      { suffix: COLUMN_SUFFIXES.value, type: fieldEnumType(false), role: 'value' },
    ],
    valueSchema: selectValueSchema,
    fromText: nonBlank((raw, options) => selectOptionFromText(raw, options)),
    toText: (value, options) => selectLabelOf(requireString(value, 'SELECT'), options),
    filterOperators: ENUMERABLE_OPERATORS,
    filterFamily: 'option',
    /**
     * The dimension the whole module is shaped around: a closed metadata option list, an
     * immutable stored id, and groups ordered by the option's INDEX in that list rather
     * than alphabetically, so a pipeline reads Lead -> Qualified -> Won (D11).
     */
    groupable: 'value',
    aggregates: UNIVERSAL_AGGREGATES,
    searchable: false,
  },

  /**
   * One column holding an ARRAY of the field's enum type, GIN-indexed in phase
   * B. A junction construction would multiply tables for what are just tags
   * (field-types.md decision 1).
   */
  MULTI_SELECT: {
    physicalColumns: () => [
      { suffix: COLUMN_SUFFIXES.value, type: fieldEnumType(true), role: 'value' },
    ],
    valueSchema: (options) => z.array(selectValueSchema(options)),
    fromText: nonBlank(multiSelectFromText),
    /** Labels joined by `;` (D37). A cell REPLACES on update; the importer never merges. */
    toText: (value, options) => {
      if (!Array.isArray(value)) {
        throw new TypeError(`MULTI_SELECT value is not an array: ${JSON.stringify(value)}`);
      }
      return value
        .map((id) => selectLabelOf(requireString(id, 'MULTI_SELECT'), options))
        .join(DEFAULT_MULTI_SELECT_SEPARATOR);
    },
    filterOperators: MULTI_SELECT_OPERATORS,
    filterFamily: 'option',
    /**
     * Not in v1: a row belongs to N groups, so the group counts sum to more than the
     * record count - a number on screen that does not add up, which needs its own UI
     * sentence before it needs its own SQL. Sorting is already refused for the sibling
     * reason, "a set has no order" (D11, section 12).
     */
    groupable: 'none',
    aggregates: UNIVERSAL_AGGREGATES,
    searchable: false,
  },

  /**
   * Only MANY_TO_ONE owns storage: a uuid column plus an FK and an index on the
   * many side. ONE_TO_MANY is the virtual reverse of one of those, and
   * MANY_TO_MANY stores its links in a junction OBJECT (INV-9). Without settings
   * we cannot know the kind, so the conservative answer is "no column".
   */
  RELATION: {
    physicalColumns: (options) =>
      options?.relation?.kind === 'MANY_TO_ONE'
        ? [{ suffix: COLUMN_SUFFIXES.relationId, type: staticType('uuid'), role: 'value' }]
        : [],
    valueSchema: () => z.uuid(),
    fromText: nonBlank((raw, options) => relationFromText(raw, options)),
    /** The target UUID: the label is not unique and cannot be the reference (D9, D37). */
    toText: (value) => requireString(value, 'RELATION'),
    filterOperators: ENUMERABLE_OPERATORS,
    filterFamily: 'id',
    /**
     * MANY_TO_ONE only, and the TYPE cannot say so: the kind lives in per-field settings,
     * and `filterRefsOf` already refuses the two kinds that own no column before any
     * grouping code runs. Cardinality is bounded by the target object rather than by the
     * type, so the group cap is what actually protects this one (D11, D20).
     */
    groupable: 'value',
    aggregates: UNIVERSAL_AGGREGATES,
    searchable: false,
  },

  EMAIL: {
    physicalColumns: () => singleColumn('text'),
    valueSchema: () => z.email(),
    fromText: nonBlank((raw) =>
      emailSchema.safeParse(raw).success
        ? parsedValue(raw)
        : refuse('INVALID_EMAIL', `"${raw}" is not an email address`),
    ),
    toText: (value) => requireString(value, 'EMAIL'),
    filterOperators: TEXTUAL_OPERATORS,
    filterFamily: 'text',
    /** Free text with a shape. Unbounded cardinality all the same (D11). */
    groupable: 'none',
    aggregates: UNIVERSAL_AGGREGATES,
    searchable: true,
  },

  /**
   * Canonical E.164 in the value column (this is what identity resolution
   * matches on), the user's own spelling beside it (field-types.md decision 3).
   */
  PHONE: {
    physicalColumns: () => [
      { suffix: COLUMN_SUFFIXES.value, type: staticType('text'), role: 'value' },
      { suffix: COLUMN_SUFFIXES.original, type: staticType('text'), role: 'original' },
    ],
    valueSchema: () => phoneValueSchema,
    /**
     * THE IDENTITY FUNCTION (D8 property 4). `toFieldWrite` already accepts a plain
     * string for PHONE and derives the canonical form itself, so a second parse here
     * would be a second place for two spellings of one number to disagree.
     */
    fromText: nonBlank((raw) => parsedValue(raw)),
    /** E.164 is the importable column; the spelling as entered is the second one (D37). */
    toText: (value) => {
      if (typeof value === 'string') return value;
      const parsed = phoneValueSchema.safeParse(value);
      if (!parsed.success) {
        throw new TypeError(`PHONE value is not { canonical, original }: ${JSON.stringify(value)}`);
      }
      return parsed.data.canonical ?? parsed.data.original;
    },
    filterOperators: TEXTUAL_OPERATORS,
    filterFamily: 'text',
    /** One value per person, near enough: unbounded cardinality (D11). */
    groupable: 'none',
    aggregates: UNIVERSAL_AGGREGATES,
    searchable: true,
  },

  URL: {
    physicalColumns: () => singleColumn('text'),
    valueSchema: () => z.url(),
    fromText: nonBlank((raw) =>
      urlSchema.safeParse(raw).success
        ? parsedValue(raw)
        : refuse('INVALID_URL', `"${raw}" is not a URL`),
    ),
    toText: (value) => requireString(value, 'URL'),
    filterOperators: TEXTUAL_OPERATORS,
    filterFamily: 'text',
    /** Unbounded cardinality (D11). */
    groupable: 'none',
    aggregates: UNIVERSAL_AGGREGATES,
    searchable: true,
  },

  /**
   * Composite and integral: never a float. The currency code travels with the
   * amount so a report can refuse to add EUR to USD instead of quietly summing
   * them (field-types.md decision 4).
   */
  CURRENCY: {
    physicalColumns: (options) => [
      { suffix: COLUMN_SUFFIXES.amountMicros, type: staticType('bigint'), role: 'amountMicros' },
      {
        suffix: COLUMN_SUFFIXES.currencyCode,
        type: staticType('char(3)'),
        role: 'currencyCode',
        defaultSql: currencyDefaultSql(options?.defaultCurrencyCode),
      },
    ],
    valueSchema: () => currencyValueSchema,
    fromText: nonBlank(currencyFromText),
    /** The decimal amount; the ISO code is the second column (D37). */
    toText: (value) => {
      const parsed = currencyValueSchema.safeParse(value);
      if (!parsed.success) {
        throw new TypeError(
          `CURRENCY value is not { amountMicros, currencyCode }: ${JSON.stringify(value)}`,
        );
      }
      return microsToDecimalText(parsed.data.amountMicros);
    },
    filterOperators: NUMERIC_OPERATORS,
    filterFamily: 'currency',
    /** An amount column is as wide as its row count, like NUMBER (D11). */
    groupable: 'none',
    /**
     * The arithmetic four are legal AND they split by currency code: `sum(amount_micros)`
     * across mixed codes is meaningless, so a CURRENCY aggregate is a set of CONDITIONAL
     * aggregates, one `filter (where currency_code = $k)` expression per discovered code,
     * all in ONE row per group (D2, D12 semantic decision 4). `aggregateSplitsByCurrency`
     * in `record-group.ts` is where that rule is written down.
     */
    aggregates: NUMERIC_AGGREGATES,
    searchable: false,
  },

  /**
   * THE THIRTEENTH TYPE (`docs/decisions/file-attachments.md` D1, D10).
   *
   * One jsonb column holding an array of server-enriched items - the first jsonb RECORD
   * column in the product (`provenance` is jsonb but it is a system column, not a field's).
   * Everything an attachment IS, is a value in a field of a record: there is no attachments
   * panel with a data source of its own, because a side table a user adds to would be a
   * mutation of record data outside `records/` (INV-1) and a third event emitter (INV-4).
   *
   * The BYTES are not in here and never touch the database. They live on a disk volume
   * behind a four-method driver (D2), and this cell holds the reference plus the three
   * immutable facts a screen needs to render a row without a join.
   */
  FILES: {
    physicalColumns: () => singleColumn('jsonb'),
    valueSchema: (options) => z.array(recordFileItemSchema).max(maxFilesOf(options?.maxFiles)),
    /**
     * ALWAYS A REFUSAL, whatever the cell says (D12). The mapping screen refuses first with
     * section 12's sentence, so this never fires in practice - but a field the importer
     * silently skipped is Twenty's trap 7, and a typed code is what makes the skip
     * impossible rather than unlikely.
     */
    fromText: () => ({
      ok: false,
      code: 'NOT_IMPORTABLE',
      message: 'files columns cannot be imported: a CSV can carry a filename but not the file',
    }),
    /**
     * The LABELS, joined by `'; '` (D12), in the one column the export layout emits under a
     * `<name> files` header that makes it non-importable. Labels rather than ids, because an
     * id means nothing outside this instance; labels rather than URLs, because a URL that
     * needs a session is useless in a spreadsheet and one that does not is a capability leak.
     */
    toText: (value) => {
      if (!Array.isArray(value)) {
        throw new TypeError(`FILES value is not an array: ${JSON.stringify(value)}`);
      }
      return value
        .map((item) => {
          const parsed = recordFileItemSchema.safeParse(item);
          if (!parsed.success) {
            throw new TypeError(`FILES item is not an attachment: ${JSON.stringify(item)}`);
          }
          return parsed.data.label;
        })
        .join(`${DEFAULT_MULTI_SELECT_SEPARATOR} `);
    },
    filterOperators: FILES_OPERATORS,
    filterFamily: 'files',
    /**
     * A dimension needs a bounded number of values AND every group expressible as a filter
     * condition on the same field. An attachment SET is neither.
     */
    groupable: 'none',
    aggregates: FILES_AGGREGATES,
    /**
     * D10, and filename search is a named non-goal (section 11). `searchTextFrom`
     * concatenates searchable COLUMNS; a jsonb array is not a string, so making this true
     * means a generated column or a jsonb-to-text expression in the vector build - a schema
     * change for every object with a FILES field.
     */
    searchable: false,
  },
} satisfies Record<FieldType, FieldTypeDef>;

export type FieldTypeRegistry = typeof FIELD_TYPE_REGISTRY;

/**
 * The operators the registry declares for ONE field type, as a literal union.
 *
 * Read off the registry object rather than written down a second time, which is
 * what makes the two filter backends provably complete (INV-6): each of them
 * declares its handler table `satisfies FilterHandlerMap<...>` below, so an
 * operator added to a registry entry fails to compile in BOTH backends until each
 * implements it, and a handler for an operator the type does not declare fails too.
 */
export type OperatorsFor<T extends FieldType> = FieldTypeRegistry[T]['filterOperators'][number];

/**
 * INV-6's compile-time half: the exact shape of a filter backend. `H` is whatever
 * one backend's handler is - a predicate over a stored value for the JS evaluator,
 * a SQL fragment builder for the compiler.
 */
export type FilterHandlerMap<H> = {
  readonly [T in FieldType]: { readonly [O in OperatorsFor<T>]: H };
};

export function fieldTypeDef(fieldType: FieldType): FieldTypeDef {
  return FIELD_TYPE_REGISTRY[fieldType];
}

export function filterFamilyOf(fieldType: FieldType): FilterFamily {
  return fieldTypeDef(fieldType).filterFamily;
}

/** Operators this field type declares, widened for runtime iteration. */
export function filterOperatorsOf(fieldType: FieldType): readonly FilterOperator[] {
  return fieldTypeDef(fieldType).filterOperators;
}

/** How this field type may be a group dimension, if at all (D11, D14). */
export function groupableKindOf(fieldType: FieldType): GroupableKind {
  return fieldTypeDef(fieldType).groupable;
}

/** Whether a field of this type may be a group dimension at all. */
export function isGroupableFieldType(fieldType: FieldType): boolean {
  return groupableKindOf(fieldType) !== 'none';
}

/** Aggregates this field type declares, widened for runtime iteration (D12, D14). */
export function aggregatesFor(fieldType: FieldType): readonly AggregateOp[] {
  return fieldTypeDef(fieldType).aggregates;
}

/** Physical columns a field of this type and configuration owns. */
export function fieldPhysicalColumns(
  fieldType: FieldType,
  options?: FieldTypeOptions,
): readonly PhysicalColumnSpec[] {
  return fieldTypeDef(fieldType).physicalColumns(options);
}

/** True when one logical value is spread over more than one column. */
export function isCompositeFieldType(fieldType: FieldType): boolean {
  return fieldPhysicalColumns(fieldType, undefined).length > 1;
}

/** Whether values of this type feed the record's search vector. */
export function isSearchableFieldType(fieldType: FieldType): boolean {
  return fieldTypeDef(fieldType).searchable;
}

/**
 * The column role the search vector reads for a searchable type. PHONE indexes
 * the ORIGINAL spelling: users search for the number the way they wrote it down,
 * while the canonical E.164 form is what identity resolution matches on.
 */
export function searchColumnRole(fieldType: FieldType): PhysicalColumnRole {
  return fieldType === 'PHONE' ? 'original' : 'value';
}

/**
 * One cell of text as a stored value (D8). Read through `fieldTypeDef` for the same
 * reason `supportsOperator` does: the per-entry literal types are narrower than the
 * interface and would reject a widened caller.
 */
export function fieldValueFromText(
  fieldType: FieldType,
  raw: string,
  options: FieldTypeOptions = {},
  format: ColumnFormat = DEFAULT_COLUMN_FORMAT,
): TextParse {
  return fieldTypeDef(fieldType).fromText(raw, options, format);
}

/** One stored value as the cell a re-import reads back (D37). */
export function fieldValueToText(
  fieldType: FieldType,
  value: unknown,
  options: FieldTypeOptions = {},
): string {
  if (value === null || value === undefined) return '';
  return fieldTypeDef(fieldType).toText(value, options);
}

/** Whether the UI may offer `operator` for `fieldType`. */
export function supportsOperator(fieldType: FieldType, operator: FilterOperator): boolean {
  // Read through fieldTypeDef so the operator list is widened to
  // readonly FilterOperator[]; indexing the registry directly keeps the narrow
  // per-entry tuple type and makes `includes` reject valid operators.
  return fieldTypeDef(fieldType).filterOperators.includes(operator);
}

/**
 * Whether the UI may offer `op` on a field of `fieldType`. The twin of
 * `supportsOperator`, read through `fieldTypeDef` for the same widening reason.
 *
 * `count` answers FALSE for every type, and that is the right answer rather than a gap:
 * it names no field, so "may I aggregate this FIELD with `count`" is not a question about
 * it (D12).
 */
export function supportsAggregate(fieldType: FieldType, op: AggregateOp): boolean {
  return fieldTypeDef(fieldType).aggregates.includes(op);
}

export const fieldTypeSchema = z.literal([...FIELD_TYPES]);
export const filterOperatorSchema = z.literal([...FILTER_OPERATORS]);
export const relationKindSchema = z.literal([...RELATION_KINDS]);
export const aggregateOpSchema = z.literal([...AGGREGATE_OPS]);
export const groupableKindSchema = z.literal([...GROUPABLE_KINDS]);
